import { useEffect, useMemo, useRef, useState } from 'react';
import { Copy, Inbox, Link2, Network, X } from 'lucide-react';
import {
  CommerceCatalogWorkspace,
  DescriptionText,
  HorizontalStack,
  PropertyGrid,
  PropertyItem,
  RegistryEmptyState,
  RegistryStatus,
  Surface,
  VerticalStack,
  toast,
} from '@workspace/silk-ui';
import { useScope } from '@workspace/scope-context';
import { useNavigation } from '@/context/NavigationContext';
import { useCommerceRegistryQuery } from '@/hooks/useCommerceRegistryQuery';
import { lastPageIndexForTotal, toLimitOffset } from '@/lib/routePagination';
import { Button } from '@/components/ui/button';
import {
  buildCommerceSectionRoute,
  buildCommerceShellViewModel,
  CommerceBreadcrumbHeader,
  formatRelativeTime,
} from '@/views/commerce/catalogHierarchyShared';
import {
  approveTenantPartnerRequest,
  createTenantPartnerInvite,
  listLinkedResellers,
  listTenantPartnerInvites,
  listTenantPartnerRequests,
  rejectTenantPartnerRequest,
  revokeTenantPartnerRequest,
  type PartnerGrantRecord,
  type PartnerInviteRecord,
  PartnerLinkError,
} from '@/lib/partner-links';
import { CommerceRegistrySearchField } from './CommerceRegistrySearchField';

function statusVariant(status?: string | null): 'default' | 'success' | 'warning' | 'error' | 'muted' {
  switch ((status || '').toLowerCase()) {
    case 'active':
    case 'approved':
      return 'success';
    case 'requested':
      return 'warning';
    case 'rejected':
    case 'revoked':
    case 'expired':
    case 'consumed':
      return 'muted';
    default:
      return 'default';
  }
}

function formatGrantType(grantType: string): string {
  return grantType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function buildInviteUrl(token: string): string {
  if (typeof window === 'undefined') {
    return `/join/partner?partner_invite=${encodeURIComponent(token)}`;
  }
  return `${window.location.origin}/join/partner?partner_invite=${encodeURIComponent(token)}`;
}

export default function LinkedResellersPage() {
  const { activeTenant } = useScope();
  const navigation = useNavigation();
  const [linkedResellers, setLinkedResellers] = useState<PartnerGrantRecord[]>([]);
  const [requests, setRequests] = useState<PartnerGrantRecord[]>([]);
  const [invites, setInvites] = useState<PartnerInviteRecord[]>([]);
  const [latestInvite, setLatestInvite] = useState<PartnerInviteRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [mutating, setMutating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessDeniedStatus, setAccessDeniedStatus] = useState<number | null>(null);
  const [totalLinkedResellers, setTotalLinkedResellers] = useState(0);
  const query = useCommerceRegistryQuery({
    facetKeys: [],
    sortFields: ['created_at', 'receiving_tenant_name', 'updated_at'],
  });
  const requestSequence = useRef(0);

  const loadData = async () => {
    const requestId = ++requestSequence.current;
    if (!activeTenant?.id) return;
    setLoading(true);
    setError(null);
    setAccessDeniedStatus(null);
    try {
      const [nextLinkedResellers, nextRequests, nextInvites] = await Promise.all([
        listLinkedResellers(activeTenant.id, {
          search: query.search || undefined,
          sort: query.sort,
          ...toLimitOffset(query.pagination),
        }),
        listTenantPartnerRequests(activeTenant.id, ['requested', 'approved', 'rejected', 'revoked']),
        listTenantPartnerInvites(activeTenant.id),
      ]);
      if (requestId !== requestSequence.current) return;
      const nextPageIndex = lastPageIndexForTotal(nextLinkedResellers.total_count, query.pagination.pageSize);
      if (query.pagination.pageIndex > nextPageIndex) {
        query.onPaginationChange(current => ({ ...current, pageIndex: nextPageIndex }));
        return;
      }
      setLinkedResellers(nextLinkedResellers.items);
      setTotalLinkedResellers(nextLinkedResellers.total_count);
      setRequests(nextRequests ?? []);
      setInvites(nextInvites ?? []);
    } catch (err) {
      if (requestId !== requestSequence.current) return;
      if (err instanceof PartnerLinkError && (err.status === 401 || err.status === 403)) {
        setAccessDeniedStatus(err.status);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load linked reseller directory.');
      }
      setLinkedResellers([]);
      setTotalLinkedResellers(0);
      setRequests([]);
      setInvites([]);
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (!activeTenant?.id) {
      setLinkedResellers([]);
      setTotalLinkedResellers(0);
      setRequests([]);
      setInvites([]);
      setLatestInvite(null);
      setLoading(false);
      setError(null);
      setAccessDeniedStatus(null);
      return;
    }
    void loadData();
  }, [activeTenant?.id, query.pagination, query.search, query.sort]);

  const pendingRequests = useMemo(
    () => requests.filter(item => item.status === 'requested'),
    [requests],
  );

  const shellViewModel = useMemo(
    () =>
      buildCommerceShellViewModel({
        activeTenant,
        accessDeniedStatus,
        activeSectionId: 'linked_resellers',
        searchQuery: query.search,
        searchPlaceholder: 'Search linked resellers...',
        onSearchChange: query.setSearch,
        error,
        blockingReason: 'Your current role does not have permission to inspect linked resellers.',
        emptyStateLabel: 'Select a tenant to manage downstream reseller links.',
        onSelectSection: sectionId => navigation.navigate(buildCommerceSectionRoute(sectionId)),
        primaryAction: {
          id: 'create-linked-reseller-invite',
          label: mutating ? 'Creating…' : 'Create Invite Link',
          disabled: !activeTenant?.id || mutating,
          onAction: () => {
            if (!activeTenant?.id || mutating) return;
            setMutating(true);
            void createTenantPartnerInvite(activeTenant.id, {
              grantType: 'reseller_visibility',
              approvalRequired: true,
              maxUses: 1,
              metadata: null,
            })
              .then(invite => {
                setLatestInvite(invite);
                toast.success('Invite link created', {
                  description: 'Share this link with the downstream reseller tenant owner.',
                });
                return loadData();
              })
              .catch(err => {
                setError(err instanceof Error ? err.message : 'Failed to create linked reseller invite.');
              })
              .finally(() => setMutating(false));
          },
        },
        metrics: [
          { id: 'linked-resellers', label: 'Linked Resellers', value: String(totalLinkedResellers) },
          { id: 'pending-requests', label: 'Pending Requests', value: String(requests.filter(item => item.status === 'requested').length) },
          { id: 'active-links', label: 'Active Invite Links', value: String(invites.filter(item => item.status === 'active').length) },
        ],
      }),
    [accessDeniedStatus, activeTenant, error, invites, mutating, navigation, query.search, query.setSearch, requests, totalLinkedResellers],
  );

  const updateRequest = async (requestId: string, action: 'approve' | 'reject' | 'revoke') => {
    if (!activeTenant?.id) return;
    setMutating(true);
    try {
      if (action === 'approve') {
        await approveTenantPartnerRequest(activeTenant.id, requestId);
      } else if (action === 'reject') {
        await rejectTenantPartnerRequest(activeTenant.id, requestId);
      } else {
        await revokeTenantPartnerRequest(activeTenant.id, requestId);
      }
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update reseller link request.');
    } finally {
      setMutating(false);
    }
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  return (
    <CommerceCatalogWorkspace
      viewModel={shellViewModel}
      breadcrumb={
        <CommerceBreadcrumbHeader
          items={[{ label: 'Commerce', href: '/commerce/catalogs' }, { label: 'Linked Resellers' }]}
        />
      }
    >
      <VerticalStack gap={6} className="p-6">
        {/* New invite banner */}
        {latestInvite ? (
          <Surface className="border-border/60 bg-muted/20 p-5">
            <VerticalStack gap={3}>
              <HorizontalStack gap={2} className="items-start justify-between">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.24em] text-muted-foreground/60">
                    Invite Link Ready
                  </p>
                  <DescriptionText className="mt-1 text-sm">
                    Share this URL with the downstream reseller. Accepting creates a visibility request — it does
                    not grant access to this tenant.
                  </DescriptionText>
                </div>
                <button
                  type="button"
                  aria-label="Dismiss invite banner"
                  onClick={() => setLatestInvite(null)}
                  className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              </HorizontalStack>
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded-md border border-border/50 bg-background px-3 py-2 text-xs text-foreground">
                  {buildInviteUrl(latestInvite.inviteToken)}
                </code>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void copyToClipboard(buildInviteUrl(latestInvite.inviteToken), 'Invite URL')}
                >
                  <Copy className="mr-1.5 h-3.5 w-3.5" />
                  Copy
                </Button>
              </div>
            </VerticalStack>
          </Surface>
        ) : null}

        {/* Linked resellers */}
        <Surface className="border-border/60 p-5">
          <VerticalStack gap={4}>
            <HorizontalStack gap={2} className="items-center justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.24em] text-muted-foreground/60">
                  Linked Resellers
                </p>
                <DescriptionText className="mt-0.5 text-sm">
                  Approved downstream reseller tenants that can be targeted in owner-authored entitlements.
                </DescriptionText>
              </div>
              {loading ? <RegistryStatus label="Loading" variant="default" /> : null}
            </HorizontalStack>

            <CommerceRegistrySearchField
              value={query.search}
              onChange={query.setSearch}
              placeholder="Search all linked resellers..."
              resultCount={totalLinkedResellers}
            />

            {!loading && linkedResellers.length === 0 ? (
              <div>
                <RegistryEmptyState
                  icon={Network}
                  title={query.search ? 'No matching linked resellers' : 'No linked resellers'}
                  description={query.search
                    ? 'No approved downstream tenants matched the current search.'
                    : 'No approved downstream tenants yet. Create an invite link to get started.'}
                />
                {query.search ? (
                  <div className="text-center">
                    <button type="button" onClick={query.clear} className="text-xs font-semibold text-primary hover:underline">
                      Reset search
                    </button>
                  </div>
                ) : null}
              </div>
            ) : linkedResellers.length > 0 ? (
              <div className="divide-y divide-border/40">
                {linkedResellers.map(item => (
                  <div key={item.id} className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-foreground">
                        {item.receivingTenantName || item.receivingTenantSlug || item.receivingTenantId}
                      </p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {item.receivingTenantSlug ? `@${item.receivingTenantSlug}` : item.receivingTenantId}
                        {item.approvedAt ? ` · Linked ${formatRelativeTime(item.approvedAt)}` : ''}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <RegistryStatus label={item.status} variant={statusVariant(item.status)} />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={mutating}
                        onClick={() => void updateRequest(item.id, 'revoke')}
                        className="hover:border-destructive hover:text-destructive"
                      >
                        Revoke
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
            <div className="flex items-center justify-between gap-3 border-t border-border/40 pt-3">
              <button
                type="button"
                onClick={() => query.onPaginationChange(current => ({ ...current, pageIndex: Math.max(0, current.pageIndex - 1) }))}
                disabled={loading || query.pagination.pageIndex === 0}
                className="rounded-md border border-border/60 px-3 py-1.5 text-xs font-semibold text-foreground disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-xs font-medium tabular-nums text-muted-foreground">
                {query.pagination.pageIndex + 1} / {lastPageIndexForTotal(totalLinkedResellers, query.pagination.pageSize) + 1}
              </span>
              <button
                type="button"
                onClick={() => query.onPaginationChange(current => ({ ...current, pageIndex: current.pageIndex + 1 }))}
                disabled={loading || query.pagination.pageIndex >= lastPageIndexForTotal(totalLinkedResellers, query.pagination.pageSize)}
                className="rounded-md border border-border/60 px-3 py-1.5 text-xs font-semibold text-foreground disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </VerticalStack>
        </Surface>

        {/* Pending requests + Invite links */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Pending requests */}
          <Surface className="border-border/60 p-5">
            <VerticalStack gap={4}>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.24em] text-muted-foreground/60">
                  Pending Requests
                </p>
                <DescriptionText className="mt-0.5 text-sm">
                  Requests created when downstream tenants accept a visibility invite link.
                </DescriptionText>
              </div>

              {pendingRequests.length === 0 ? (
                <RegistryEmptyState
                  icon={Inbox}
                  title="No pending requests"
                  description="Requests appear here when a reseller redeems an invite link."
                />
              ) : (
                <VerticalStack gap={2}>
                  {pendingRequests.map(item => (
                    <Surface key={item.id} className="border-border/50 p-4">
                      <VerticalStack gap={3}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-foreground">
                              {item.receivingTenantName || item.receivingTenantSlug || item.receivingTenantId}
                            </p>
                            <p className="mt-0.5 text-xs text-muted-foreground">
                              Requested {formatRelativeTime(item.requestedAt)}
                            </p>
                          </div>
                          <RegistryStatus label={item.status} variant={statusVariant(item.status)} />
                        </div>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            size="sm"
                            disabled={mutating}
                            onClick={() => void updateRequest(item.id, 'approve')}
                          >
                            Approve
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={mutating}
                            onClick={() => void updateRequest(item.id, 'reject')}
                          >
                            Reject
                          </Button>
                        </div>
                      </VerticalStack>
                    </Surface>
                  ))}
                </VerticalStack>
              )}
            </VerticalStack>
          </Surface>

          {/* Invite links */}
          <Surface className="border-border/60 p-5">
            <VerticalStack gap={4}>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.24em] text-muted-foreground/60">
                  Invite Links
                </p>
                <DescriptionText className="mt-0.5 text-sm">
                  Previously issued seller-visibility links for downstream reseller tenants.
                </DescriptionText>
              </div>

              {invites.length === 0 ? (
                <RegistryEmptyState
                  icon={Link2}
                  title="No invite links"
                  description="Use 'Create Invite Link' in the toolbar to issue a new one."
                />
              ) : (
                <VerticalStack gap={2}>
                  {invites.map(item => (
                    <Surface key={item.id} className="border-border/50 p-4">
                      <VerticalStack gap={3}>
                        <div className="flex items-center justify-between gap-4">
                          <p className="text-sm font-semibold text-foreground">
                            {formatGrantType(item.grantType)}
                          </p>
                          <RegistryStatus label={item.status} variant={statusVariant(item.status)} />
                        </div>
                        <PropertyGrid cols={item.expiresAt ? 3 : 2}>
                          <PropertyItem
                            label="Uses"
                            value={`${item.useCount}${item.maxUses ? ` / ${item.maxUses}` : ''}`}
                          />
                          <PropertyItem label="Created" value={formatRelativeTime(item.createdAt)} />
                          {item.expiresAt ? (
                            <PropertyItem label="Expires" value={formatRelativeTime(item.expiresAt)} />
                          ) : null}
                        </PropertyGrid>
                        {item.status === 'active' ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="w-fit"
                            onClick={() => void copyToClipboard(buildInviteUrl(item.inviteToken), 'Invite URL')}
                          >
                            <Copy className="mr-1.5 h-3.5 w-3.5" />
                            Copy URL
                          </Button>
                        ) : null}
                      </VerticalStack>
                    </Surface>
                  ))}
                </VerticalStack>
              )}
            </VerticalStack>
          </Surface>
        </div>
      </VerticalStack>
    </CommerceCatalogWorkspace>
  );
}
